import clsx from "clsx";
import { memo, useCallback, useMemo, useState } from "react";

import { Icon } from "@atoms/icon";
import { Modal } from "@atoms/modal";
import { Typography } from "@atoms/typography";
import { FastButton } from "@fastFields/fastButton";
import { FastLinkButton } from "@fastFields/fastLinkButton";
import {
  IProductKeyIconsColorEnum,
  IProductKeyIconsSizeEnum,
  ProductKeyIcons,
} from "@molecules/productKeyIcons";
import { getAlertIconProps } from "@organisms/alert/helper";
import {
  IAlertAlignmentEnum,
  IAlertButtonsTypeEnum,
  IAlertVariantsEnum,
} from "@organisms/alert/types";

import classes from "./styles.module.scss";

import type React from "react";

import type { IAlertProps } from "@organisms/alert/types";

export const Alert = memo(function Alert({
  title,
  children,
  alignment = IAlertAlignmentEnum.Horizontal,
  buttonsType = IAlertButtonsTypeEnum.Basic,
  cancelButtonProps,
  linkButtonType,
  submitButtonProps,
  customIcon,
  noIcon,
  description,
  variant = IAlertVariantsEnum.Information,
  noMaxWidth,
  customChildren,
  ...modalProps
}: IAlertProps) {
  const iconProps = useMemo(
    () => getAlertIconProps(variant, customIcon),
    [variant, customIcon],
  );
  const noButtons = useMemo(
    () => !cancelButtonProps && !submitButtonProps,
    [cancelButtonProps, submitButtonProps],
  );

  const handleSubmitClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      try {
        setIsLoading(true);
        await submitButtonProps?.onClick?.(event);
        setIsLoading(false);
      } catch {
        setIsLoading(false);
      }
    },
    [submitButtonProps],
  );

  const [isLoading, setIsLoading] = useState(false);

  return (
    <Modal maxWidth={noMaxWidth ? undefined : "27.5rem"} {...modalProps}>
      <div className={clsx(classes.alert)}>
        <div data-alignment={alignment} className={clsx(classes.alert__header)}>
          {!noIcon &&
            (variant === IAlertVariantsEnum.CustomIcon ? (
              <ProductKeyIcons
                iconName={customIcon?.name || "EyeLine"}
                color={IProductKeyIconsColorEnum.Gray}
                size={IProductKeyIconsSizeEnum.Medium}
              />
            ) : (
              <div data-variant={variant} className={clsx(classes.alert__icon)}>
                <Icon {...iconProps} />
              </div>
            ))}
          <div
            className={clsx(classes.alert__content)}
            data-alignment={alignment}
          >
            <Typography fontType={"label-medium"}>{title}</Typography>
            {description && (
              <Typography fontType={"body-small"}>{description}</Typography>
            )}
            {children}
          </div>
        </div>
        {!noButtons && (
          <div className={clsx(classes.alert__actions)}>
            {linkButtonType && (
              <div>
                <FastLinkButton type={linkButtonType} />
              </div>
            )}
            <div className={clsx(classes.alert__mainActions)}>
              {cancelButtonProps && (
                <FastButton
                  {...cancelButtonProps}
                  className={clsx(
                    buttonsType === IAlertButtonsTypeEnum.Stretch &&
                      classes.alert__grow,
                    cancelButtonProps.className,
                  )}
                />
              )}
              {submitButtonProps && (
                <FastButton
                  isLoading={isLoading}
                  {...submitButtonProps}
                  onClick={handleSubmitClick}
                  className={clsx(
                    buttonsType === IAlertButtonsTypeEnum.Stretch &&
                      classes.alert__grow,
                    submitButtonProps.className,
                  )}
                />
              )}
            </div>
          </div>
        )}
        {customChildren}
      </div>
    </Modal>
  );
});
Alert.displayName = "Alert";
